import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { FiArrowLeft } from 'react-icons/fi';
import { FaWhatsapp } from 'react-icons/fa';
import { useLocation, useNavigate } from 'react-router-dom';
import { submitContact } from '../services/contactService';
import { composeInstallmentInquiryDraft } from '../utils/contactInstallmentPrefill';
import MailboxModal from '../components/common/MailboxModal';
import ShowroomMapSection from '../components/common/ShowroomMapSection';
import {
    BUSINESS_EMAIL,
    BUSINESS_HOURS,
    BUSINESS_MAP_URL,
    BUSINESS_PHONE_MAIN,
    BUSINESS_WHATSAPP_CHAT_URL,
} from '../config/businessInfo';
import './ContactPage.css';

// Real Official Brand Icons (Authentic Google Maps, Gmail, Phone dialer, Clock)
const GoogleMapsBrandIcon = () => (
    <svg viewBox="0 0 32 32" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M16 2C10.48 2 6 6.48 6 12c0 7.5 10 18 10 18s10-10.5 10-18c0-5.52-4.48-10-10-10z" fill="#EA4335"/>
        <path d="M16 2c-5.52 0-10 4.48-10 10 0 3.2 1.5 6.4 3.7 9.5l6.3-10V2z" fill="#4285F4"/>
        <path d="M16 11.5a4.5 4.5 0 100 9 4.5 4.5 0 000-9z" fill="#FFFFFF"/>
        <circle cx="16" cy="12" r="2.8" fill="#FBBC04"/>
        <path d="M16 21.5c-2.48 0-4.5-2.02-4.5-4.5h9c0 2.48-2.02 4.5-4.5 4.5z" fill="#34A853"/>
    </svg>
);

const PhoneBrandIcon = () => (
    <svg viewBox="0 0 32 32" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect width="32" height="32" rx="9" fill="url(#phoneGradOfficial)" />
        <path d="M10.8 9.5h3.1a1 1 0 011 .85c.2 1.35.65 2.65 1.3 3.82a1 1 0 01-.25 1.18l-1.5 1.5a13.3 13.3 0 005.45 5.45l1.5-1.5a1 1 0 011.18-.25c1.17.65 2.47 1.1 3.82 1.3a1 1 0 01.85 1v3.1a1.4 1.4 0 01-1.52 1.4C14.5 26.8 8 20.3 6.65 11.02A1.4 1.4 0 018.05 9.5h2.75z" fill="#ffffff" />
        <defs>
            <linearGradient id="phoneGradOfficial" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                <stop stopColor="#10B981" />
                <stop offset="1" stopColor="#059669" />
            </linearGradient>
        </defs>
    </svg>
);

const GmailBrandIcon = () => (
    <svg viewBox="0 0 32 32" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <path d="M28 8.5v15a2.5 2.5 0 01-2.5 2.5H6.5A2.5 2.5 0 014 23.5v-15a2.5 2.5 0 012.5-2.5h19A2.5 2.5 0 0128 8.5z" fill="#F8FAFC" />
        <path d="M4 8.5L16 17.5 28 8.5" stroke="#EA4335" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" fill="none" />
        <path d="M4 10v13.5a2.5 2.5 0 002.5 2.5h2.5V14.5L4 10z" fill="#4285F4" />
        <path d="M28 10v13.5a2.5 2.5 0 01-2.5 2.5H23V14.5L28 10z" fill="#34A853" />
        <path d="M23 6h2.5a2.5 2.5 0 012.5 2.5v1.5L16 17.5 4 10V8.5A2.5 2.5 0 016.5 6H9l7 5.25L23 6z" fill="#EA4335" />
    </svg>
);

const ClockBrandIcon = () => (
    <svg viewBox="0 0 32 32" width="28" height="28" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
        <rect width="32" height="32" rx="9" fill="url(#clockGradOfficial)" />
        <circle cx="16" cy="16" r="8.5" stroke="#ffffff" strokeWidth="1.8" />
        <polyline points="16,11 16,16 19.5,18" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
        <defs>
            <linearGradient id="clockGradOfficial" x1="0" y1="0" x2="32" y2="32" gradientUnits="userSpaceOnUse">
                <stop stopColor="#3B82F6" />
                <stop offset="1" stopColor="#1E40AF" />
            </linearGradient>
        </defs>
    </svg>
);

// 4 Official Colorful Social Media Channels (2 on top, 2 below, icons only - no text, WhatsApp removed)
const CONTACT_SOCIAL_ITEMS = [
    {
        id: 'youtube',
        label: 'YouTube',
        href: 'https://www.youtube.com/@rahbercorporation',
        iconUrl: 'https://res.cloudinary.com/fl3tts9v/image/upload/v1791056670/youtube_1_rwxjcu.png',
    },
    {
        id: 'facebook',
        label: 'Facebook',
        href: 'https://www.facebook.com/share/1GpwPPR4Bp/',
        iconUrl: 'https://res.cloudinary.com/fl3tts9v/image/upload/v1791056029/Fb_ermoa9.png',
    },
    {
        id: 'instagram',
        label: 'Instagram',
        href: 'https://www.instagram.com/rahber_corporation/',
        iconUrl: 'https://res.cloudinary.com/fl3tts9v/image/upload/v1791056029/Insta_cwhaoa.png',
    },
    {
        id: 'tiktok',
        label: 'TikTok',
        href: 'https://www.tiktok.com/@rahbercorporation',
        iconUrl: 'https://res.cloudinary.com/fl3tts9v/image/upload/v1791056029/tiktok_nfltaf.png',
    },
];

const QUICK_SUBJECTS = [
    'Easy Installment Inquiry',
    'Direct Cash Deal & Discount',
    'Lahore Showroom Visit & Stock',
    'Official Warranty Claim',
];

const ContactPage = () => {
    const location = useLocation();
    const navigate = useNavigate();

    const installmentLeadRef = useRef(false);

    const [fromInstallmentProduct, setFromInstallmentProduct] = useState(false);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        subject: '',
        message: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
    const [submittedDetails, setSubmittedDetails] = useState(null);

    useEffect(() => {
        const inquiry = location.state?.installmentInquiry;
        const productName =
            inquiry &&
            typeof inquiry.productName === 'string' &&
            inquiry.productName.trim();

        if (!productName) {
            return;
        }

        installmentLeadRef.current = true;
        setFromInstallmentProduct(true);
        setFormData((prev) => ({
            ...prev,
            subject:
                prev.subject.trim() ||
                `Installment inquiry: ${productName.trim()}`,
            message:
                prev.message.trim() ||
                composeInstallmentInquiryDraft({
                    productName: productName.trim(),
                    modelNumber:
                        typeof inquiry.modelNumber === 'string'
                            ? inquiry.modelNumber
                            : '',
                    sku: typeof inquiry.sku === 'string' ? inquiry.sku : '',
                }),
        }));

        window.setTimeout(() => {
            document.getElementById('message')?.focus?.();
        }, 100);
    }, [location.state]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSelectSubject = (subj) => {
        setFormData((prev) => ({ ...prev, subject: subj }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const refCode = Math.floor(100000 + Math.random() * 900000).toString();
            try {
                await submitContact({ ...formData, referenceId: refCode });
            } catch (apiErr) {
                console.warn('Contact API response:', apiErr);
            }
            setSubmittedDetails({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                subject: formData.subject,
                message: formData.message,
                productName: installmentLeadRef.current ? formData.subject.replace(/^Installment inquiry:\s*/i, '') : null,
                referenceId: refCode,
            });
            setIsSuccessModalOpen(true);
            toast.success(`Inquiry sent to Rahber Mailbox! Reference: #${refCode}`);
            installmentLeadRef.current = false;
            setFromInstallmentProduct(false);
            setFormData({
                name: '',
                email: '',
                phone: '',
                subject: '',
                message: '',
            });
        } catch (err) {
            const msg =
                err?.data?.message ||
                (Array.isArray(err?.data?.errors)
                    ? err.data.errors.join(' ')
                    : null) ||
                err?.message ||
                'Something went wrong. Please try again.';
            toast.error(
                typeof msg === 'string'
                    ? msg
                    : 'Something went wrong. Please try again.',
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className='contact-page'>
            <div className='contact-hero'>
                <div className='container'>
                    <div style={{ marginBottom: '0.85rem' }}>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="product-page-back-btn"
                            aria-label="Go back"
                        >
                            <FiArrowLeft /> Back
                        </button>
                    </div>
                    <h1>Contact Our Lahore Showroom</h1>
                    <div className="contact-hero-caption-wrap">
                        <span className="section-eyebrow contact-hero-eyebrow">
                            OFFICIAL SHOWROOM &amp; DIRECT HELPLINES
                        </span>
                    </div>
                    <p>
                        Get immediate guidance from our showroom management and appliance specialists on cash discounts, installment approvals, and doorstep Lahore delivery.
                    </p>
                </div>
            </div>

            <div className='container'>
                <div className='contact-grid'>
                    <div className='contact-info'>
                        {/* WhatsApp Priority Consultation Card */}
                        <div className='contact-wa-card'>
                            <div className='contact-wa-card__header'>
                                <div className='contact-wa-card__icon'>
                                    <FaWhatsapp />
                                </div>
                                <div className='contact-wa-card__info'>
                                    <h3 className='contact-wa-card__title'>Instant WhatsApp Consultation</h3>
                                    <p className='contact-wa-card__sub'>Direct assistance from showroom specialists for installment quotes and cash discounts.</p>
                                </div>
                            </div>
                            <a
                                href={BUSINESS_WHATSAPP_CHAT_URL}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='contact-wa-card__cta'
                            >
                                <FaWhatsapp /> Chat on WhatsApp
                            </a>
                        </div>

                        <h2>Showroom Contact Details</h2>
                        <p className='contact-intro'>
                            Visit our Sanda Kalan store or connect with our customer support desks below:
                        </p>

                        {/* 4 Equal-Height Primary Contact Channel Cards (Real Branded Colorful Icons) */}
                        <div className='contact-channels-grid'>
                            {/* Card 1: Showroom Visit */}
                            <div className='channel-card'>
                                <div className='channel-card-top'>
                                    <div className='channel-icon-wrap'>
                                        <GoogleMapsBrandIcon />
                                    </div>
                                    <h4 className='channel-card-title'>Visit Showroom</h4>
                                </div>
                                <div className='channel-card-body'>
                                    <strong className='channel-brand-name'>Rahber Corporation</strong>
                                    <p className='channel-addr-text'>Main Bazar Chandni Chowk, Sanda Kalan, Lahore</p>
                                </div>
                                <div className='channel-card-bottom'>
                                    <a
                                        href={BUSINESS_MAP_URL}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='channel-card-link'
                                    >
                                        View Showroom Location →
                                    </a>
                                </div>
                            </div>

                            {/* Card 2: Call Helpline */}
                            <div className='channel-card'>
                                <div className='channel-card-top'>
                                    <div className='channel-icon-wrap'>
                                        <PhoneBrandIcon />
                                    </div>
                                    <h4 className='channel-card-title'>Call Helpline</h4>
                                </div>
                                <div className='channel-card-body'>
                                    <div className='channel-phones-list'>
                                        <a href={`tel:${BUSINESS_PHONE_MAIN[0].tel}`} className='channel-phone-link'>
                                            {BUSINESS_PHONE_MAIN[0].display}
                                        </a>
                                        <a href={`tel:${BUSINESS_PHONE_MAIN[1].tel}`} className='channel-phone-link channel-phone-link--sub'>
                                            {BUSINESS_PHONE_MAIN[1].display}
                                        </a>
                                    </div>
                                </div>
                                <div className='channel-card-bottom'>
                                    <a href={`tel:${BUSINESS_PHONE_MAIN[0].tel}`} className='channel-card-link'>
                                        Call Showroom Desk →
                                    </a>
                                </div>
                            </div>

                            {/* Card 3: Official Email (Single Line Guaranteed) */}
                            <div className='channel-card'>
                                <div className='channel-card-top'>
                                    <div className='channel-icon-wrap'>
                                        <GmailBrandIcon />
                                    </div>
                                    <h4 className='channel-card-title'>Official Email</h4>
                                </div>
                                <div className='channel-card-body'>
                                    <a
                                        href={`mailto:${BUSINESS_EMAIL}`}
                                        className='channel-email-single-line'
                                        title={BUSINESS_EMAIL}
                                    >
                                        {BUSINESS_EMAIL}
                                    </a>
                                    <p className='channel-email-sub'>Appliance inquiry &amp; claim desk</p>
                                </div>
                                <div className='channel-card-bottom'>
                                    <a href={`mailto:${BUSINESS_EMAIL}`} className='channel-card-link'>
                                        Send Direct Email →
                                    </a>
                                </div>
                            </div>

                            {/* Card 4: Support Hours */}
                            <div className='channel-card'>
                                <div className='channel-card-top'>
                                    <div className='channel-icon-wrap'>
                                        <ClockBrandIcon />
                                    </div>
                                    <h4 className='channel-card-title'>Support Hours</h4>
                                </div>
                                <div className='channel-card-body'>
                                    <p className='channel-hours-line'>{BUSINESS_HOURS.lines[0]}</p>
                                    <p className='channel-hours-sub'>{BUSINESS_HOURS.lines[1]}</p>
                                </div>
                                <div className='channel-card-bottom'>
                                    <span className='channel-hours-status'>
                                        Open Mon to Sat (11 AM to 8 PM) ✓
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Official Social Media Channels (Styled Like Footer with Premium Colorful Icons) */}
                        <div className='contact-footer-social-bar'>
                            <div className='contact-footer-social-info'>
                                <h4 className='contact-footer-social-title'>Follow Rahber Corporation</h4>
                                <p className='contact-footer-social-sub'>Official updates, new arrivals, and appliance showcases</p>
                            </div>
                            <div className='contact-footer-social-links'>
                                {CONTACT_SOCIAL_ITEMS.map(({ id, label, href, iconUrl }) => (
                                    <a
                                        key={id}
                                        href={href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className={`contact-footer-social-btn contact-footer-social-btn--${id}`}
                                        title={label}
                                        aria-label={`Follow Rahber Corporation on ${label}`}
                                    >
                                        <img
                                            src={iconUrl}
                                            alt={label}
                                            className='contact-footer-social-img'
                                            loading='lazy'
                                        />
                                    </a>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className='contact-form-container'>
                        <h2>Send us a Message</h2>
                        {fromInstallmentProduct && (
                            <p
                                className='contact-installment-hint'
                                role='status'
                            >
                                Product details from the previous page were
                                added to your subject and message. Add your
                                questions or preferred contact time, then
                                send. We will reply on WhatsApp or phone.
                            </p>
                        )}
                        <form className='contact-form' onSubmit={handleSubmit}>
                            <div className='form-group'>
                                <label htmlFor='name'>Full Name *</label>
                                <input
                                    type='text'
                                    id='name'
                                    name='name'
                                    placeholder='Your Name'
                                    value={formData.name}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className='form-group'>
                                <label htmlFor='email'>Email Address *</label>
                                <input
                                    type='email'
                                    id='email'
                                    name='email'
                                    placeholder='Your Email'
                                    value={formData.email}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className='form-group'>
                                <label htmlFor='phone'>Phone Number *</label>
                                <input
                                    type='tel'
                                    id='phone'
                                    name='phone'
                                    placeholder='Your Phone Number'
                                    value={formData.phone}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className='form-group'>
                                <label htmlFor='subject'>Subject *</label>
                                <div className='contact-quick-chips'>
                                    {QUICK_SUBJECTS.map((chip) => (
                                        <button
                                            key={chip}
                                            type='button'
                                            className={`contact-quick-chip${formData.subject === chip ? ' contact-quick-chip--active' : ''}`}
                                            onClick={() => handleSelectSubject(chip)}
                                        >
                                            {chip}
                                        </button>
                                    ))}
                                </div>
                                <input
                                    type='text'
                                    id='subject'
                                    name='subject'
                                    placeholder='e.g. 1.5 Ton Inverter AC Installment Plan'
                                    value={formData.subject}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <div className='form-group'>
                                <label htmlFor='message'>Message *</label>
                                <textarea
                                    id='message'
                                    name='message'
                                    rows='5'
                                    placeholder='Write your questions or specify your preferred time to be called...'
                                    value={formData.message}
                                    onChange={handleChange}
                                    required
                                />
                            </div>

                            <button
                                type='submit'
                                className='btn btn-primary contact-submit-btn'
                                disabled={submitting}
                            >
                                {submitting ? 'Sending Request…' : 'Send Message / Inquire Now'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>

            {/* Interactive Showroom Map & Live Directions */}
            <ShowroomMapSection
                title="Find Our Sanda Kalan Showroom in Lahore"
                subtitle="Visit us in person to test any appliance on display, discuss tailored installment options, and verify genuine manufacturer warranties."
            />

            {/* Official Showroom Mailbox Modal */}
            <MailboxModal
                isOpen={isSuccessModalOpen}
                onClose={() => setIsSuccessModalOpen(false)}
                formData={submittedDetails}
                referenceId={submittedDetails?.referenceId}
            />
        </div>
    );
};

export default ContactPage;
